import { NgClass, NgFor } from '@angular/common';
import {
    Component,
    input,
    output,
    forwardRef,
    signal,
    OnInit,
    effect,
    untracked,
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';

/** Opção de uma lista de seleção única (radio). */
export type RadioOption = { label: string; value: any };

/**
 * Lista de opções de seleção única (radio), integrável a formulários
 * (`ControlValueAccessor`).
 *
 * Mantém a opção selecionada sincronizada mesmo quando {@link options} é
 * recarregada com novas referências (re-casa pela igualdade de `value`/`value.id`).
 */
@Component({
    selector: 'ai-radio-list',
    templateUrl: 'radio-list.component.html',
    imports: [NgFor, NgClass, ReactiveFormsModule],
    styleUrls: ['radio-list.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RadioList),
            multi: true,
        },
    ],
})
export class RadioList implements ControlValueAccessor, OnInit {
    /** Classe CSS extra aplicada ao grupo. */
    cssClass = input<string>('');

    /** Opções disponíveis para seleção. */
    options = input<RadioOption[]>([]);

    /** Opção selecionada inicialmente. */
    defaultValue = input<RadioOption>();

    /** Emite a opção sempre que a seleção muda. */
    selectionChange = output<RadioOption>();

    /** Opção atualmente selecionada. */
    option = signal<RadioOption>({ label: '', value: undefined });

    /** Callback de mudança do ControlValueAccessor. */
    onChange: (value: RadioOption) => void = () => {};

    /** Callback de "touched" do ControlValueAccessor. */
    onTouched: () => void = () => {};

    constructor() {
        effect(() => {
            const opts = this.options();
            if (opts.length === 0) return;
            const current = untracked(() => this.option());

            if (current.value === undefined) {
                // BehaviorSubject estava vazio no mount: ngOnInit não gravou seleção.
                // Aplica defaultValue buscando-o em opts pelo id para manter referência consistente.
                const dv = untracked(() => this.defaultValue());
                if (!dv) return;
                const match = opts.find(o =>
                    o.value?.id !== undefined ? o.value.id === dv.value?.id : o.value === dv.value
                );
                if (match) this.option.set(match);
                return;
            }

            // Caso normal: options refreshou com novas referências. Re-sincroniza.
            const match = opts.find(o =>
                o.value?.id !== undefined
                    ? o.value.id === current.value?.id
                    : o.value === current.value
            );
            if (match && match.value !== current.value) {
                this.option.set(match);
            }
        });
    }

    onSelectionChange(option: RadioOption) {
        this.option.set(option);
        this.onChange(this.option());
        this.onTouched();
        this.selectionChange.emit(this.option());
    }

    ngOnInit() {
        if (this.defaultValue()) {
            this.writeValue(this.defaultValue());
        }
    }

    writeValue(value: any): void {
        if (value.value !== undefined) {
            this.onSelectionChange(value);
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        // Implement if needed
    }
}
